import { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';

// Dashboard → API endpoint mapping (using proxy to avoid CORS)
const FEEDBACK_ENDPOINTS: Record<string, string> = {
  'autotask': '/api/proxy/feedback?target=autotask',
  'itglue': '/api/proxy/feedback?target=itglue',
  // Add other dashboards as they get feedback endpoints:
  // '365mgmt': '/api/proxy/feedback?target=365mgmt',
};

// Widget ID to display name mapping
const WIDGET_NAMES: Record<string, string> = {
  'autotask': 'AutoTask Tickets',
  '365mgmt': '365 Management',
  'itglue': 'ITGlue Activity',
  'screenconnect': 'ScreenConnect Security',
  'serverhealth': 'Server Health',
};

type SubmitStatus = 'idle' | 'capturing' | 'submitting' | 'success' | 'error';

interface FeedbackWidgetProps {
  hidden?: boolean;
}

export default function FeedbackWidget({ hidden = false }: FeedbackWidgetProps) {
  const location = useLocation();
  const { user } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Extract widget ID from path: /dashboard/autotask → 'autotask'
  const getActiveWidgetId = useCallback(() => {
    const match = location.pathname.match(/^\/dashboard\/(.+)$/);
    return match ? match[1] : null;
  }, [location.pathname]);

  const getWidgetName = useCallback(() => {
    const widgetId = getActiveWidgetId();
    if (!widgetId) return 'Dashboard';
    return WIDGET_NAMES[widgetId] || widgetId;
  }, [getActiveWidgetId]);

  const getFeedbackEndpoint = useCallback(() => {
    const widgetId = getActiveWidgetId();
    if (!widgetId) return null;
    return FEEDBACK_ENDPOINTS[widgetId] || null;
  }, [getActiveWidgetId]);

  // Capture screenshot using Screen Capture API
  const captureScreenshot = async (): Promise<string | null> => {
    try {
      // Request screen capture - user picks screen/window/tab
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'browser',
        },
        audio: false,
      });

      // Get the video track
      const track = stream.getVideoTracks()[0];

      // Create a video element to capture a frame
      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();

      // Wait a moment for the video to be ready
      await new Promise(resolve => setTimeout(resolve, 100));

      // Create canvas and draw the video frame
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Could not get canvas context');
      }

      ctx.drawImage(video, 0, 0);

      // Stop the stream immediately after capture
      track.stop();
      stream.getTracks().forEach(t => t.stop());

      // Convert to data URL
      const dataUrl = canvas.toDataURL('image/png', 0.8);
      return dataUrl;
    } catch (err) {
      console.error('Screen capture failed:', err);
      // User cancelled or permission denied
      if (err instanceof Error && err.name === 'NotAllowedError') {
        return null; // User cancelled - not an error
      }
      throw err;
    }
  };

  const handleOpen = async () => {
    setStatus('capturing');
    setDescription('');
    setErrorMessage('');
    setScreenshot(null);

    try {
      const dataUrl = await captureScreenshot();

      if (dataUrl) {
        setScreenshot(dataUrl);
        setIsOpen(true);
        setStatus('idle');
      } else {
        // User cancelled the screen picker
        setStatus('idle');
        // Don't open the modal if they cancelled
      }
    } catch (err) {
      console.error('Failed to capture screenshot:', err);
      // Still open the modal but show error
      setIsOpen(true);
      setScreenshot(null);
      setStatus('idle');
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setScreenshot(null);
    setDescription('');
    setStatus('idle');
    setErrorMessage('');
  };

  const handleRetakeScreenshot = async () => {
    setStatus('capturing');
    try {
      const dataUrl = await captureScreenshot();
      if (dataUrl) {
        setScreenshot(dataUrl);
      }
      setStatus('idle');
    } catch (err) {
      console.error('Failed to retake screenshot:', err);
      setStatus('idle');
    }
  };

  const handleSubmit = async () => {
    const endpoint = getFeedbackEndpoint();

    if (!endpoint) {
      setErrorMessage('Feedback is not available for this dashboard yet.');
      setStatus('error');
      return;
    }

    if (!description.trim()) {
      setErrorMessage('Please enter a description.');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const payload = {
        screenshot: screenshot,
        description: description.trim(),
        pageUrl: location.pathname,
        pageTitle: getWidgetName(),
        userName: user?.name || 'Unknown',
        userEmail: user?.username || 'unknown@unknown.com',
        browserInfo: navigator.userAgent,
        submittedAt: new Date().toISOString(),
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      setStatus('success');

      // Close after showing success message
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      setErrorMessage(err instanceof Error ? err.message : 'Failed to submit feedback');
      setStatus('error');
    }
  };

  const widgetId = getActiveWidgetId();
  const endpoint = getFeedbackEndpoint();
  const isAvailable = !!endpoint;

  // Hide when review overlay is active
  if (hidden) {
    return null;
  }

  return (
    <div className="feedback-widget">
      {/* Floating button */}
      <button
        className="feedback-button"
        onClick={handleOpen}
        disabled={status === 'capturing'}
        title="Send Feedback"
        aria-label="Send Feedback"
      >
        {status === 'capturing' ? (
          <div className="loading-spinner" style={{ width: 20, height: 20 }}></div>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        )}
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="feedback-modal-overlay" onClick={handleClose}>
          <div className="feedback-modal" onClick={e => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <h2>Send Feedback</h2>
              <button className="feedback-modal-close" onClick={handleClose} aria-label="Close">
                &times;
              </button>
            </div>

            <div className="feedback-modal-body">
              {/* Screenshot preview */}
              <div className="feedback-screenshot-section">
                <div className="feedback-screenshot-header">
                  <label>Screenshot</label>
                  {screenshot && (
                    <button
                      className="feedback-retake-button"
                      onClick={handleRetakeScreenshot}
                      disabled={status === 'capturing' || status === 'submitting'}
                    >
                      Retake
                    </button>
                  )}
                </div>
                {status === 'capturing' ? (
                  <div className="feedback-screenshot-loading">
                    <div className="loading-spinner"></div>
                    <span>Select a screen to capture...</span>
                  </div>
                ) : screenshot ? (
                  <div className="feedback-screenshot-preview">
                    <img src={screenshot} alt="Screenshot preview" />
                  </div>
                ) : (
                  <div className="feedback-screenshot-error">
                    <p>No screenshot captured</p>
                    <button
                      className="feedback-retake-button"
                      onClick={handleRetakeScreenshot}
                    >
                      Take Screenshot
                    </button>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="feedback-description-section">
                <label htmlFor="feedback-description">Description</label>
                <textarea
                  id="feedback-description"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Describe the issue or suggestion..."
                  rows={4}
                  disabled={status === 'submitting' || status === 'success'}
                />
              </div>

              {/* Dashboard info */}
              <div className="feedback-info">
                <span className="feedback-info-label">Sending to:</span>
                <span className="feedback-info-value">
                  {widgetId ? getWidgetName() : 'Main Dashboard'}
                  {!isAvailable && widgetId && (
                    <span className="feedback-unavailable"> (not available yet)</span>
                  )}
                </span>
              </div>

              {/* Error message */}
              {status === 'error' && errorMessage && (
                <div className="feedback-error">
                  {errorMessage}
                </div>
              )}

              {/* Success message */}
              {status === 'success' && (
                <div className="feedback-success">
                  Feedback submitted successfully!
                </div>
              )}
            </div>

            <div className="feedback-modal-footer">
              <button
                className="feedback-cancel-button"
                onClick={handleClose}
                disabled={status === 'submitting'}
              >
                Cancel
              </button>
              <button
                className="feedback-submit-button"
                onClick={handleSubmit}
                disabled={!isAvailable || status === 'capturing' || status === 'submitting' || status === 'success'}
              >
                {status === 'submitting' ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
